import { captureAction } from '@smg-real-estate/browser-rum';

export const measureAsyncCall = async <T>(
  apiCall: () => Promise<T>,
  actionLabel: string,
): Promise<T> => {
  const startTime = Date.now();

  try {
    const result = await apiCall();
    const endTime = Date.now();
    const responseTimeMs = endTime - startTime;

    // Log the action and responseTimeMs to Datadog
    captureAction(actionLabel, { responseTimeMs });

    return result;
  } catch (error) {
    const endTime = Date.now();
    const responseTimeMs = endTime - startTime;

    // Log failure with responseTimeMs
    captureAction(`${actionLabel}-failed`, { responseTimeMs });

    // Re-throw the error to handle it in the caller
    throw error;
  }
};

export enum LoadTrackerEventName {
  getToken = 'getToken',
  getUserDetails = 'getUserDetails',
  userInit = 'userInit',
}

// This function tracks in milliseconds
export const initialLoadTracker = (() => {
  const events: Record<string, number> = {};
  const resetTracker = () => {
    Object.keys(events).forEach((key) => delete events[key]);
  };

  return {
    // Starts tracking the entire process
    start: () => {
      if (!events['start']) events['start'] = performance.now();
    },

    // Tracks an individual event start
    trackEvent: (eventName: LoadTrackerEventName) => {
      const currentTime = performance.now();

      if (!events['start']) events['start'] = currentTime;
      events[eventName] = currentTime;
    },

    // Ends an event and calculates duration
    endEvent: (eventName: LoadTrackerEventName) => {
      if (!events[eventName]) return;
      events[eventName] = performance.now() - events[eventName];
    },

    // Ends tracking and logs everything to Datadog
    end: () => {
      const startTime = events['start'];
      if (!startTime) return resetTracker();

      const totalLoadTime = performance.now() - startTime;
      const trackedTotal = Object.values(LoadTrackerEventName).reduce((sum, eventName) => {
        return sum + (events[eventName] || 0);
      }, 0);
      const additionalWorkTimeMs = Math.max(0, totalLoadTime - trackedTotal);

      const logOutput: Record<string, number> = {
        additionalWorkTimeMs,
      };
      Object.values(LoadTrackerEventName).forEach((eventName) => {
        logOutput[`${eventName}TimeMs`] = events[eventName] || 0;
      });

      captureAction('initial-load-tracker', logOutput);

      resetTracker();
    },
  };
})();
